import type {HTMLInputAttributes} from 'svelte/elements';
import type {Action} from 'svelte/action';
import {type VariantProps, tv} from 'tailwind-variants';
import type {Class} from 'Catalyst/class';
import {default as BaseInput} from './BaseInput.svelte';
import {
  baseInputSlotsBaseResizingW,
  baseInputSlotsBaseResizingH,
  baseInputSlotsLabelWrapperResizingW,
  baseInputSlotsLabelWrapperResizingH,
} from 'Catalyst/tokens/output/base-input';

export const baseInputTvProps = tv({
  slots: {
    base: `
      flex items-center relative
      w-full
    `,
    inputWrapper: `
      w-full
      rounded-[--base-input-slots-base-border-radius]
      outline
      [outline-width:--base-input-slots-base-outline-width]
      outline-offset-[--base-input-slots-base-outline-offset]
      border-none
      bg-[--base-input-slots-base-bg-color]
      flex items-center
      group
    `,
    input: `
      ${baseInputSlotsBaseResizingW}
      ${baseInputSlotsBaseResizingH}
      w-full
      peer
      text-[--base-input-slots-input-value-color]
      [font:--base-input-slots-input-value-typography]
      font-[--base-input-slots-input-value-font-weight]
      placeholder:text-[--base-input-slots-placeholder-color]
      placeholder:[font:--base-input-slots-placeholder-typography]
      placeholder:font-[--base-input-slots-placeholder-font-weight]
      gap-[--base-input-slots-base-item-spacing]
      px-[--base-input-slots-base-px]
      focus:ring-offset-0
      focus:ring-0
      border-none
      outline-none
      bg-transparent
    `,
    label: `
      ${baseInputSlotsLabelWrapperResizingW}
      ${baseInputSlotsLabelWrapperResizingH}
      text-md
      bg-white
      absolute
      left-0
      duration-100
      ease-linear
      rounded-[--base-input-slots-label-wrapper-border-radius]
      text-[--base-input-slots-label-color]
      [font:--base-input-slots-label-typography]
      [font-weight:--base-input-slots-label-font-weight]
      bg-[--base-input-slots-label-wrapper-bg-color]
      group-focus-within:text-[--base-input-variants-state-focus-label-color]
      peer-invalid:text-[--base-input-variants-state-invalid-label-color]
      peer-[:invalid:hover]:text-[--base-input-variants-state-invalid-label-color]
      peer-[:invalid:active]:text-[--base-input-variants-state-invalid-label-color]
      peer-[:invalid:focus]:bg-[--base-input-variants-floating-label-true-label-wrapper-bg-color]
      peer-[:invalid:focus]:text-[--base-input-variants-state-invalid-label-color]
      peer-[:invalid:focus]:[font:--base-input-variants-floating-label-true-label-typography]
    `,
  },
  variants: {
    size: {
      '40dp': {
        input: `
          h-[--base-input-variants-size-40dp-base-height]
        `,
      },
    },
    disabled: {
      false: {
        inputWrapper: `
          [&:has(>:focus)]:bg-[--base-input-slots-base-bg-color]
        `,
        label: `
          text-[--base-input-slots-label-color]
        `,
      },
      true: {
        inputWrapper: `
          bg-[--base-input-variants-disabled-true-base-bg-color]
          outline-[--base-input-variants-disabled-true-base-outline-color]
        `,
        label: `
          text-[--base-input-variants-disabled-true-label-color]
        `,
      },
    },
    floating: {
      false: {
        label: `
          translate-x-[--base-input-slots-base-px]
          translate-y-1/2
          [font:--base-input-slots-label-typography]
          [font-weight:--base-input-slots-label-font-weight]
          px-[--base-input-slots-label-wrapper-px]
          group-focus-within:translate-x-[--base-input-variants-floating-label-true-label-wrapper-position-x]
          group-focus-within:translate-y-[--base-input-variants-floating-label-true-label-wrapper-position-y]
          group-focus-within:bg-[--base-input-variants-floating-label-true-label-wrapper-bg-color]
          group-focus-within:[font:--base-input-variants-floating-label-true-label-typography]
          group-focus-within:px-[--base-input-variants-floating-label-true-label-wrapper-px]
        `,
      },
      true: {
        label: `
          translate-x-[--base-input-variants-floating-label-true-label-wrapper-position-x]
          translate-y-[--base-input-variants-floating-label-true-label-wrapper-position-y]
          bg-[--base-input-variants-floating-label-true-label-wrapper-bg-color]
          [font:--base-input-variants-floating-label-true-label-typography]
          px-[--base-input-variants-floating-label-true-label-wrapper-px]
        `,
      },
    },
    invalid: {
      true: {
        inputWrapper: `
          outline-[--base-input-variants-state-invalid-base-outline-color]
          hover:bg-[--base-input-variants-state-invalid-hover-base-bg-color]
          hover:outline-[--base-input-variants-state-invalid-base-outline-color]
          [&:has(input:active)]:bg-[--base-input-variants-state-invalid-active-base-bg-color]
          [&:has(>input:active)]:outline-[--base-input-variants-state-invalid-base-outline-color]
          focus-within:outline-[--base-input-variants-state-invalid-base-outline-color]
          focus-within:[outline-width:--base-input-variants-state-focus-base-outline-width]
          focus-within:outline-offset-[--base-input-variants-state-focus-base-outline-offset]
        `,
        input: `
          text-[--base-input-variants-state-invalid-input-value-color]
          placeholder:text-[--base-input-variants-state-invalid-placeholder-color]
        `,
        label: `
          text-[--base-input-variants-state-invalid-label-color]
          group-focus-within:text-[--base-input-variants-state-invalid-label-color]
        `,
      },
    },
  },
  compoundVariants: [
    {
      disabled: false,
      invalid: false,
      class: {
        inputWrapper: `
          bg-[--base-input-slots-base-bg-color]
          outline-[--base-input-slots-base-outline-color]
          hover:bg-[--base-input-variants-state-hover-base-bg-color]
          [&:has(input:active)]:bg-[--base-input-variants-state-active-base-bg-color]
          focus-within:outline-[--base-input-variants-state-focus-base-outline-color]
          focus-within:[outline-width:--base-input-variants-state-focus-base-outline-width]
          focus-within:outline-offset-[--base-input-variants-state-focus-base-outline-offset]
        `,
      },
    },
  ],
  defaultVariants: {
    size: '40dp',
    disabled: false,
    invalid: false,
  },
});

export type BaseInputTvProps = VariantProps<typeof baseInputTvProps>;

export type BaseInputOwnProps = Partial<{
  label: string;
  inputTestId: string;
  labelTestId: string;
  ref: HTMLInputElement;
  action: Action;
  floating: boolean;
}>;

export type BaseInputProps = Omit<HTMLInputAttributes, 'size'> &
  BaseInputTvProps &
  BaseInputOwnProps &
  Class;

export {BaseInput};
